/**
 * react-intl provider wired to browser preference with Japanese
 * fallback. Locale JSON files are small and pre-loaded (no async
 * split) — switching locale is instant.
 *
 * `src/i18n/translations/{en,ja,pt-BR}.json` ARE the catalogs — they
 * are imported directly below. `pnpm i18n:extract` collects new ids
 * into `lang/extracted.json` for translation; there is no compile
 * step, and nothing reads a precompiled AST (the old `compiled/`
 * output was unused and drifted, removed 2026-09-21).
 *
 * A message with no catalog entry falls back to its `defaultMessage`,
 * so the UI stays readable while a translation is pending —
 * `src/v2/i18n-completeness.test.ts` fails the build if a ja or pt-BR
 * key is missing.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';

import en from './translations/en.json';
import ja from './translations/ja.json';
import ptBR from './translations/pt-BR.json';

type Locale = 'en' | 'pt-BR' | 'ja';

const SUPPORTED: Locale[] = ['en', 'pt-BR', 'ja'];
const DEFAULT: Locale = 'ja';

// New users land in Japanese — the operators are JP-based and the
// English/PT versions exist for the staff team plus a small set of
// foreign workers. Browser language detection is intentionally NOT
// used for the first visit; users switch via the LanguageMenu and
// the choice is persisted to `relay.locale`.
function detectLocale(): Locale {
  const saved = (typeof localStorage !== 'undefined'
    ? localStorage.getItem('relay.locale')
    : null) as Locale | null;
  if (saved && SUPPORTED.includes(saved)) return saved;
  return DEFAULT;
}

const messages: Record<Locale, Record<string, string>> = {
  en: en as Record<string, string>,
  'pt-BR': ptBR as Record<string, string>,
  ja: ja as Record<string, string>,
};

export function IntlProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(detectLocale);

  useEffect(() => {
    localStorage.setItem('relay.locale', locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale]);

  return (
    <LocaleContext.Provider value={value}>
      <ReactIntlProvider
        locale={locale}
        defaultLocale={DEFAULT}
        messages={messages[locale]}
      >
        {children}
      </ReactIntlProvider>
    </LocaleContext.Provider>
  );
}

import { createContext, useContext } from 'react';

const LocaleContext = createContext<{
  locale: Locale;
  setLocale: (l: Locale) => void;
}>({ locale: DEFAULT, setLocale: () => {} });

export function useLocaleSwitch() {
  return useContext(LocaleContext);
}
